import React from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { bool, func, number, shape, string } from 'prop-types';
import * as R from 'ramda';
import { useParams } from 'react-router-dom';

// utils
import { capitalize, formatNumber } from 'utils/helpers/format';
import { formatEndDate, formatLastUsed } from 'feature/loading-management/utils/timeHelpers';
import { decodeURI } from 'utils/helpers/url';
import { useUserRolePermissions } from 'hooks';

import { END_DATE_FORMAT } from 'feature/loading-management/utils/const';
import { useTheme } from '@mui/material/styles';
import SupplierAuthLimitRemainingVolume from 'feature/loading-management/supplier-authorizations/cards-panel/card/card-components/SupplierAuthLimitRemainingVolume';
import {
  cardBodyWrapStyles,
  datesTextStyles,
  labelTextStyles,
  limitTextStyles,
  twoPartyLabelStyles,
  twoPartyLabelWithActionsStyles,
  twoPartyPendingStyles,
} from './Card.styles';

const boxProps = {
  alignItems: 'center',
  borderRadius: 0.5,
  display: 'flex',
  height: 61,
  justifyContent: 'center',
  mb: 1,
};

const CardBody = ({
  active,
  endDate,
  exchange,
  remainingVolume,
  lastUsed,
  limitMet,
  limitType,
  limitVolume,
  productAuthorizationId,
  setMyAuthorizationConfirmationDialog,
  twoPartyExchangeInfo,
  unlimited,
}) => {
  const theme = useTheme();
  const { viewBy, viewById } = useParams();
  const { isProductLoadingManage } = useUserRolePermissions();

  const exchangeConfirmed = exchange?.confirmed;
  const formattedEndDate = formatEndDate(END_DATE_FORMAT, endDate);
  const formattedLastUsed = formatLastUsed(lastUsed);
  const isInventoryOwner = viewBy === 'inventory-owner';
  // Due to the way the data comes in from the database, authorizations with a RenewalVolume(Database property) of null
  // will cause the limitMet to be displayed as null, which should be displayed as an authorization with maxed limit
  const validLimitMet = R.defaultTo(true, limitMet);

  const allCapsConversion = str => str[0] + R.toLower(str.substr(1));

  const handleTwoPartyClick = type => {
    setMyAuthorizationConfirmationDialog({
      header: `${capitalize(type)} Two Party Exchange?`,
      message: `${capitalize(type)} Two Party Exchange for ${
        twoPartyExchangeInfo?.title
      } loading for ${isInventoryOwner ? decodeURI(viewById) : twoPartyExchangeInfo?.subtitle} at ${
        isInventoryOwner ? twoPartyExchangeInfo?.subtitle : decodeURI(viewById)
      }?`,
      params: { productAuthorizationId, twoPartyExchangeInfo, type, viewBy, viewById },
    });
  };

  const renderLimitBody = () =>
    unlimited ? (
      <>
        <Box bgcolor="graphic.light" {...boxProps}>
          <Typography>Unlimited</Typography>
        </Box>
        <SupplierAuthLimitRemainingVolume
          active={active}
          remainingVolumeData={{ remainingVolume }}
        />
      </>
    ) : (
      <>
        <Box
          bgcolor={validLimitMet ? 'error.light' : 'success.light'}
          pb={1}
          pt="6px"
          {...boxProps}
          flexDirection="column"
        >
          <Typography sx={{ ...limitTextStyles(theme)({ validLimitMet }) }} variant="subtitle1">
            {formatNumber(parseInt(limitVolume))}
          </Typography>
          <Typography color="textPrimary" variant="body2">
            Gallon {allCapsConversion(limitType)} Limit {validLimitMet ? 'Met' : ''}
          </Typography>
        </Box>
        <SupplierAuthLimitRemainingVolume
          active={active}
          remainingVolumeData={{ remainingVolume }}
        />
      </>
    );

  const renderTwoPartyOptions = R.cond([
    [
      R.isNil,
      R.always(<Typography sx={{ ...twoPartyPendingStyles(theme) }}>Turned Off</Typography>),
    ],
    [
      R.equals(true),
      R.always(<Typography sx={{ ...twoPartyPendingStyles(theme) }}>Turned On</Typography>),
    ],
    [
      R.equals(false),
      R.always(
        <>
          <Typography sx={{ ...twoPartyPendingStyles(theme) }}>
            Pending Supplier Acceptance
          </Typography>
          {isProductLoadingManage && (
            <Box display="flex">
              <Button
                disabled={!active}
                onClick={() => handleTwoPartyClick('reject')}
                variant="text"
              >
                Reject
              </Button>
              <Button
                disabled={!active}
                onClick={() => handleTwoPartyClick('accept')}
                variant="text"
              >
                Accept
              </Button>
            </Box>
          )}
        </>
      ),
    ],
  ]);

  const twoPartyStyles = R.cond([
    [
      R.isNil,
      R.always({
        bgcolor: null,
        border: 1,
        borderColor: 'graphic.light',
        labelClass: twoPartyLabelStyles(theme),
      }),
    ],
    [
      R.equals(true),
      R.always({
        bgcolor: 'graphic.lighter',
        border: null,
        borderColor: null,
        labelClass: twoPartyLabelStyles(theme),
      }),
    ],
    [
      R.equals(false),
      R.always({
        bgcolor: 'graphic.lighter',
        border: null,
        borderColor: null,
        labelClass: twoPartyLabelWithActionsStyles(theme),
      }),
    ],
  ])(exchangeConfirmed);

  return (
    <>
      <Box sx={{ ...cardBodyWrapStyles(theme) }}>
        <Divider />
        <Divider />
        <Typography sx={{ ...datesTextStyles(theme) }} variant="body2">
          End Date
        </Typography>
        <Typography sx={{ ...datesTextStyles(theme) }} variant="body2">
          Last Used
        </Typography>
        <Typography variant="h5">{R.defaultTo('--', formattedEndDate)}</Typography>
        <Typography variant="h5">{R.defaultTo('--', formattedLastUsed)}</Typography>
      </Box>
      {isProductLoadingManage ? (
        <>
          {active ? (
            renderLimitBody()
          ) : (
            <>
              <Box border={1} borderColor="graphic.light" {...boxProps}>
                <Typography variant="subtitle1">Inactive</Typography>
              </Box>
              <SupplierAuthLimitRemainingVolume
                active={active}
                remainingVolumeData={{ remainingVolume }}
              />
            </>
          )}
          <Box
            alignItems="center"
            bgcolor={twoPartyStyles.bgcolor}
            border={twoPartyStyles.border}
            borderColor={twoPartyStyles.borderColor}
            borderRadius="4px"
            display="flex"
            flexDirection="column"
            height={71}
            justifyContent="center"
          >
            <Typography sx={twoPartyStyles.labelClass}>Two Party Exchange</Typography>
            {renderTwoPartyOptions(exchangeConfirmed)}
          </Box>
        </>
      ) : (
        <>
          <Box
            alignItems="center"
            bgcolor={active ? 'secondary.light' : 'graphic.lighter'}
            borderRadius="4px"
            display="flex"
            height="61px"
            justifyContent="center"
            my={1}
            p={0.5}
            textAlign="center"
          >
            <Typography color="textPrimary" sx={{ ...labelTextStyles }} variant="subtitle2">
              {active ? 'Active' : 'Inactive'}
            </Typography>
          </Box>
          <SupplierAuthLimitRemainingVolume
            active={active}
            remainingVolumeData={{ remainingVolume }}
          />
          <Box
            alignItems="center"
            bgcolor={twoPartyStyles.bgcolor}
            border={twoPartyStyles.border}
            borderColor={twoPartyStyles.borderColor}
            borderRadius="4px"
            display="flex"
            flexDirection="column"
            height={71}
            justifyContent="center"
            mt={1}
          >
            <Typography sx={{ ...twoPartyLabelStyles(theme) }}>Two Party Exchange</Typography>
            {renderTwoPartyOptions(exchangeConfirmed)}
          </Box>
        </>
      )}
    </>
  );
};

CardBody.propTypes = {
  active: bool.isRequired,
  endDate: string,
  exchange: shape({
    confirmed: bool,
  }),
  lastUsed: string,
  limitMet: bool,
  limitType: string,
  limitVolume: number,
  notSequencedCount: number,
  productAuthorizationId: number.isRequired,
  remainingVolume: number,
  sequencedCount: number,
  setMyAuthorizationConfirmationDialog: func.isRequired,
  twoPartyExchangeInfo: shape({}),
  unlimited: bool,
};

CardBody.defaultProps = {
  endDate: '',
  exchange: null,
  lastUsed: '',
  limitMet: null,
  limitType: '',
  limitVolume: null,
  notSequencedCount: undefined,
  remainingVolume: null,
  sequencedCount: undefined,
  twoPartyExchangeInfo: {},
  unlimited: null,
};

export default CardBody;
